import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import backdrop from '../assets/page-backdrop.webp';

const EXPANDED = 228;
const COLLAPSED = 62;

/** Below this the sidebar is off-canvas entirely; the design says ≤1024px. */
const TABLET = 1024;

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Shown as a count beside the label. Omit for none. */
  badge?: number | undefined;
}

export interface AppShellProps {
  /**
   * Which portal this is, beside the logo. Omit for none - the customer
   * portal does, since a customer only ever has this one.
   */
  portalName?: string | undefined;
  /**
   * The current screen's name, shown in the page header.
   *
   * The design gives every screen an 80px header carrying just this - not a
   * breadcrumb, not actions. It is what tells you where you are once the
   * sidebar is collapsed to icons.
   */
  title: string;
  nav: NavItem[];
  /** Rendered per item so the shell stays router-agnostic. */
  renderLink: (item: NavItem, content: ReactNode, collapsed: boolean) => ReactNode;
  logo: ReactNode;
  user?: { name: string; org?: string | undefined; role?: string | undefined } | undefined;
  /** Sits at the bottom, above the collapse toggle - sign out, theme, etc. */
  footer?: ReactNode;
  storageKey: string;
  children: ReactNode;
}

function readCollapsed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function AppShell({
  portalName,
  title,
  nav,
  renderLink,
  logo,
  user,
  footer,
  storageKey,
  children,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isTablet, setIsTablet] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= TABLET,
  );

  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${String(TABLET)}px)`);
    const onChange = (e: MediaQueryListEvent) => {
      setIsTablet(e.matches);
      // Closing on the transition, because a drawer left open while the layout
      // reflows covers the page it was navigating to.
      if (!e.matches) setMobileOpen(false);
    };
    query.addEventListener('change', onChange);
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  const toggleCollapse = useCallback(() => {
    setCollapsed((was) => {
      const next = !was;
      try {
        localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        // Private browsing. The choice still applies for this visit.
      }
      return next;
    });
  }, [storageKey]);

  // On tablet the sidebar is a drawer and always shows labels - collapsing to
  // icons in an overlay would be the worst of both.
  const showLabels = isTablet || !collapsed;
  const width = isTablet ? EXPANDED : collapsed ? COLLAPSED : EXPANDED;

  // `h-dvh` + `overflow-hidden` pins this row to exactly the viewport height,
  // so the sidebar (which just stretches to match it) never scrolls off with
  // the page. `min-h-dvh` would let a tall page grow the whole row past the
  // viewport instead, dragging the sidebar up and out of view with it.
  return (
    <div className="flex h-dvh overflow-hidden bg-background text-foreground">
      {isTablet && mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-foreground/30"
          onClick={() => {
            setMobileOpen(false);
          }}
        />
      )}

      <aside
        style={{ width }}
        className={cn(
          'z-40 flex shrink-0 flex-col border-r border-border bg-card transition-[width] duration-150',
          isTablet && 'fixed inset-y-0 left-0',
          isTablet && !mobileOpen && '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center gap-2 border-b border-border px-3">
          <span className="shrink-0">{logo}</span>
          {showLabels && portalName && (
            <span className="truncate text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
              {portalName}
            </span>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto p-2.5" aria-label="Main">
          <ul className="flex flex-col gap-0.5">
            {nav.map((item) => (
              <li key={item.to}>
                {renderLink(
                  item,
                  <>
                    <span className="flex size-4 shrink-0 items-center justify-center">
                      {item.icon}
                    </span>
                    {showLabels && <span className="truncate">{item.label}</span>}
                    {showLabels && item.badge !== undefined && item.badge > 0 && (
                      <span className="ml-auto rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold">
                        {item.badge}
                      </span>
                    )}
                  </>,
                  !showLabels,
                )}
              </li>
            ))}
          </ul>
        </nav>

        {user && (
          <div className="flex items-center gap-2.5 border-t border-border px-3 py-2.5">
            <span
              className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground"
              aria-hidden="true"
            >
              {user.name.slice(0, 2).toUpperCase()}
            </span>
            {showLabels && (
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium">{user.name}</span>
                <span className="mt-0.5 flex items-center gap-1.5">
                  {user.org && (
                    <span className="truncate text-[10.5px] text-muted-foreground">{user.org}</span>
                  )}
                  {user.role && (
                    <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      {user.role}
                    </span>
                  )}
                </span>
              </span>
            )}
          </div>
        )}

        <div className="flex flex-col gap-0.5 border-t border-border p-2.5">
          {footer}
          {!isTablet && (
            <button
              type="button"
              onClick={toggleCollapse}
              aria-expanded={!collapsed}
              className="flex h-8.5 items-center gap-2.75 rounded-lg px-2.5 text-[12.5px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <span className="flex size-4 shrink-0 items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d={collapsed ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'} />
                </svg>
              </span>
              {showLabels && <span className="truncate">Collapse</span>}
            </button>
          )}
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {isTablet && (
          <div className="flex h-14 items-center gap-3 border-b border-border px-4">
            <button
              type="button"
              aria-label="Open navigation"
              onClick={() => {
                setMobileOpen(true);
              }}
              className="flex size-9 items-center justify-center rounded-lg hover:bg-muted"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
            {logo}
          </div>
        )}

        {/*
          The page header, transcribed: 80px minimum, 18px/26px padding, and a
          19px 700-weight title at -0.3px tracking. The design puts one on every
          screen, which is what makes a collapsed sidebar survivable - the icons
          stop telling you where you are, and this does.
        */}
        <div className="flex min-h-20 flex-none items-center gap-x-6 gap-y-2.5 overflow-hidden border-b border-border bg-card px-6.5 py-4.5">
          <h1 className="min-w-0 truncate text-[19px] leading-tight font-bold tracking-[-0.3px]">
            {title}
          </h1>
        </div>

        {/*
          The backdrop is painted on `main` itself, not on an absolutely
          positioned layer inside it. A layer at `inset-0` is only as tall as
          the visible box, so it scrolled away with the content and a long page
          ran out of image into plain white. A background on the scroll
          container stays put while its content scrolls, so it always fills the
          view - no tiling seam, no stretching. `dark:bg-none` keeps the old
          behaviour of hiding it in dark mode.
        */}
        <main
          className="relative min-h-0 flex-1 overflow-auto bg-background bg-(image:--backdrop) bg-cover bg-center bg-no-repeat px-6 pt-5.5 pb-10 dark:bg-none"
          // Through a custom property rather than `backgroundImage` inline: an
          // inline style would outrank `dark:bg-none` and the image would stay.
          style={{ '--backdrop': `url(${backdrop})` } as CSSProperties}
        >
          <div className="relative min-w-0">{children}</div>
        </main>
      </div>
    </div>
  );
}

/**
 * The class string a nav row uses, exported so each portal's router link can
 * wear it without re-deriving the measurements.
 */
export function navItemClass(active: boolean, collapsed: boolean): string {
  return cn(
    'flex h-[34px] items-center gap-[11px] rounded-lg px-2.5 text-[12.5px] transition-colors',
    collapsed && 'justify-center px-0',
    active
      ? 'bg-accent font-medium text-accent-foreground'
      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
  );
}
